import React, { useEffect, useMemo, useState } from 'react';
import {
  MaterialReactTable,
  type MRT_ColumnDef, type MRT_ColumnFiltersState, type MRT_PaginationState,
  type MRT_SortingState, type MRT_TableOptions, useMaterialReactTable, MRT_Row, MRT_EditActionButtons,
} from 'material-react-table';
import {
  Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField,
  Tooltip, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useGetPrevCustElemSumByPrev,
  useUpdatePrevCustElem,
} from "./ServicesCE";
import {CustElem, PrevCustElem} from "./types";
import PreventiviEditCEROW from "./PreventiviEditCEROW";

interface PreventiviEditCEProps {
  idprevprop: number;

}

const PreventiviEditCE: React.FC<PreventiviEditCEProps> = ({ idprevprop }) => {
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({pageIndex: 0, pageSize: 15});


  const {
    data: {results = [], results_sum = [], count} = {},
    isError: isLoadingCustElemByPrevError,
    isFetching: isFetchingCustElemByPrev,
    isLoading: isLoadingCustElemByPrev
  } = useGetPrevCustElemSumByPrev(pagination, columnFilters, globalFilter, sorting, idprevprop);

  const {mutateAsync: updatePrevCustElem, isPending: isUpdatingPrevCustElem} = useUpdatePrevCustElem();

  const [currentRowId, setCurrentRowId] = useState<number | null>(null);
  const [isDialogEditOpen, setIsDialogEditOpen] = useState(false);

  // const openEditModal = (row: MRT_Row<PrevCustElem>) => {
  //      setIsDialogEditOpen(true);
  //      setCurrentRowId(row.id);
  //  };

  const openEditModal = (row: MRT_Row<PrevCustElem>) => {
    setCurrentRowId(Number(row.original.id)); // ✅ real DB id
    setIsDialogEditOpen(true);
  };


  const columns = useMemo<MRT_ColumnDef<PrevCustElem>[]>(() => [
    {
      accessorKey: 'id',
      header: 'Id',
      enableEditing: false,
      size: 30,
    },
    {
      accessorKey: 'flgprevcons',
      header: 'P/C',
      size: 40,
      editVariant: 'select',
      editSelectOptions: [
        {label: 'Preventivo', value: 'P'},
        {label: 'Consuntivo', value: 'C'},
      ],
      muiEditTextFieldProps: {
        select: true,
        required: true,
      },
      Cell: ({cell}) => {
        const value = cell.getValue<'P' | 'C'>();

        return (
            <Typography
                sx={{
                  fontWeight: 'bold',
                  color: value === 'P' ? 'green' : 'blue',
                }}
            >
              {value}
            </Typography>
        );
      },
    },
    {
      accessorKey: 'codareacust',
      header: 'CodA',
      size: 60,
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.codareacust,
        helperText: validationErrors?.codareacust,
        onFocus: () => setValidationErrors({...validationErrors, codareacust: undefined}),
      },
    },
    {
      accessorKey: 'desareacust',
      header: 'Area',
      size: 100,
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.desareacust,
        helperText: validationErrors?.desareacust,
        onFocus: () => setValidationErrors({...validationErrors, desareacust: undefined}),
      },
    },
    {
      accessorKey: 'codmodcust',
      header: 'CodM',
      size: 60,
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.codmodcust,
        helperText: validationErrors?.codmodcust,
        onFocus: () => setValidationErrors({...validationErrors, codmodcust: undefined}),
      },
    },
    {
      accessorKey: 'desmodcust',
      header: 'Modulo',
      size: 300,
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.desmodcust,
        helperText: validationErrors?.desmodcust,
        onFocus: () => setValidationErrors({...validationErrors, desmodcust: undefined}),
      },
    },
    {
      accessorKey: 'numanalisi',
      header: 'Ana',
      size: 45,
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.numanalisi,
        helperText: validationErrors?.numanalisi,
        onFocus: () => setValidationErrors({...validationErrors, numanalisi: undefined}),
      },
      muiTableBodyCellProps: {align: 'right'}, muiTableFooterCellProps: {align: 'right'},
      Footer: () => <Box>{results_sum[0]?.sum_numanalisi.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) || 0}</Box>
    },
    {
      accessorKey: 'numconf',
      header: 'Con',
      size: 45,
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.numconf,
        helperText: validationErrors?.numconf,
        onFocus: () => setValidationErrors({...validationErrors, numconf: undefined}),
      },
      muiTableBodyCellProps: {align: 'right'}, muiTableFooterCellProps: {align: 'right'},
      Footer: () => <Box>{results_sum[0]?.sum_numconf.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) || 0}</Box>
    },
    {
      accessorKey: 'numform',
      header: 'For',
      size: 45,
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.numform,
        helperText: validationErrors?.numform,
        onFocus: () => setValidationErrors({...validationErrors, numform: undefined}),
      },
      muiTableBodyCellProps: {align: 'right'}, muiTableFooterCellProps: {align: 'right'},
      Footer: () => <Box>{results_sum[0]?.sum_numform.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) || 0}</Box>
    },
    {
      accessorKey: 'numtot',
      header: 'Tot',
      size: 45,
      enableEditing: false,
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.numtot,
        helperText: validationErrors?.numtot,
        onFocus: () => setValidationErrors({...validationErrors, numtot: undefined}),
      },
      muiTableBodyCellProps: {align: 'right'}, muiTableFooterCellProps: {align: 'right'},
      Footer: () => <Box>{results_sum[0]?.sum_numtot.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) || 0}</Box>
    },
    {
      accessorKey: 'numprep',
      header: 'Prep',
      size: 45,
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.numprep,
        helperText: validationErrors?.numprep,
        onFocus: () => setValidationErrors({...validationErrors, numprep: undefined}),
      },
      muiTableBodyCellProps: {align: 'right'}, muiTableFooterCellProps: {align: 'right'},
    },
    {
      accessorKey: 'note',
      header: 'Note',
      size: 350,
      grow: true,

      Cell: ({cell}) => {
        const value = cell.getValue<string>() ?? '';

        return (
            <Tooltip
                title={value}
                arrow
                placement="top-start"
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 400,
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.85rem',
                    },
                  },
                }}
            >
              <Box
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'help',
                  }}
              >
                {value}
              </Box>
            </Tooltip>
        );
      },

      muiEditTextFieldProps: {
        required: true,
        multiline: true,
        minRows: 2,
      },

      muiTableBodyCellProps: {align: 'left'},
      muiTableFooterCellProps: {align: 'right'},
    }
  ], [validationErrors, results_sum]);


  const handleSavePrevCustElem: MRT_TableOptions<PrevCustElem>['onEditingRowSave'] = async ({values, table}) => {
    // const newValidationErrors = validatePrevCustElem(values);
    // if (Object.values(newValidationErrors).some((error) => error)) {
    //   setValidationErrors(newValidationErrors);
    //   return;
    // }
    // setValidationErrors({});

    const parseNum = (val: any) => parseFloat((val ?? '0').toString().replace(',', '.')) || 0;

    const numanalisi = parseNum(values.numanalisi);
    const numconf = parseNum(values.numconf);
    const numform = parseNum(values.numform);
    const numtot = numanalisi + numconf + numform;

    const myPrevCustElem = {
      id: values.id,
      idprevtesta: values.idprevtesta,
      desareacust: values.desareacust,
      codareacust: values.codareacust,
      desmodcust: values.desmodcust,
      codmodcust: values.codmodcust,
      numanalisi: values.numanalisi,
      numconf: values.numconf,
      numform: values.numform,
      numtot: numtot,
      numprep: values.numprep,
      note: values.note,
      codnodoareacust: values.codnodoareacust,
      codnodomodcust: values.codnodomodcust,
      seqareacust: values.seqareacust,
      seqmodcust: values.seqmodcust,
      flgprevcons: values.flgprevcons,
    };

    await updatePrevCustElem(myPrevCustElem);
    table.setEditingRow(null); // exit editing mode
  };


  const table = useMaterialReactTable({
    columns,
    data: results,
    displayColumnDefOptions: {'mrt-row-actions': {size: 40}},

    layoutMode: 'grid',
    initialState: {
      density: 'compact',
      // pagination: { pageSize: 15, pageIndex: 0 },
    },
    enableRowSelection: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableColumnActions: false,
    enableColumnOrdering: false,
    enableColumnFilterModes: false,
    enableColumnResizing: false,
    enablePagination: false,
    enableColumnVirtualization: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableToolbarInternalActions: false,
    enableEditing: true,
    editDisplayMode: 'modal',
    // muiEditRowDialogProps: {
    //
    //     open: true,
    //     maxWidth: "md", // Max width by breakpoint
    //     // slotProps:{
    //     //   paper: {
    //     //     sx: { maxWidth: 500 } // Max width in px
    //     //   }
    //     // }
    //   },
    enableTableHead: true,
    // muiTableProps: {    //   sx: {    //     border: '1px solid rgba(81, 81, 81, .5)',    //     caption: {    //       captionSide: 'top',    //     },    //   },    // },
    muiTableBodyRowProps: {hover: false},
    muiTableHeadCellProps: {
      sx: {
        border: '1px solid rgba(81, 81, 81, .5)',
        backgroundColor: 'lightgrey',
        fontStyle: 'italic',
        fontSize: '0.8rem',
        fontWeight: 'bold',
      },
    },
    muiTableBodyCellProps: {
      style: {
        border: '1px solid rgba(81, 81, 81, .5)',

      },
    },
    muiTableFooterCellProps: {
      sx: {
        border: '1px ' + 'solid rgba(81, 81, 81, .5)',
        color: 'red',
        align: 'right',
        fontWeight: 'bold',
        fontSize: '16px',
      },
    },

    getRowId: (row) => row.id,

    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleSavePrevCustElem,

    renderRowActions: ({row, table}) => (
        <Box sx={{display: 'flex', gap: '1rem'}}>
          {/*<Tooltip title="Edit">*/}
          {/*  <IconButton onClick={() => table.setEditingRow(row)}>*/}
          {/*    <EditIcon />*/}
          {/*  </IconButton>*/}
          {/*</Tooltip>*/}


          <Tooltip title="Modifica">
            <IconButton onClick={() => openEditModal(row)}>
              <EditIcon/>
            </IconButton>
          </Tooltip>

          {isDialogEditOpen && currentRowId !== null && (
              <PreventiviEditCEROW
                  open={isDialogEditOpen}
                  idprevcustelemprop={currentRowId}
                  onClose={() => {
                    setIsDialogEditOpen(false);
                    setCurrentRowId(null);
                  }}
              />
          )}


        </Box>
    ),


    state: {
      isLoading: isLoadingCustElemByPrev,
      // isSaving:  isUpdatingElemByPrev ,
      showAlertBanner: isLoadingCustElemByPrevError,
      showProgressBars: isFetchingCustElemByPrev,
    },
  });


  return (
      <Box>
        <MaterialReactTable table={table}/>

        {isDialogEditOpen && currentRowId !== null && (
            <PreventiviEditCEROW
                open={isDialogEditOpen}
                idprevcustelemprop={currentRowId}
                onClose={() => {
                  setIsDialogEditOpen(false);
                  setCurrentRowId(null);
                }}
            />
        )}
      </Box>
  );

}
export default PreventiviEditCE;

